import {
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  DataTableSkeleton,
} from '@carbon/react';
import { Section } from 'components/section';
import { Link } from 'components/link';
import { ReactNode } from 'react';
import { EmptyState } from 'components/empty-state';
import DocumentSvg from 'images/srp-illustration-document.svg';
import { useAtomValue } from 'jotai';
import { useSupportingFilesAtom } from './atoms';
import { WellSupportingFileResponse } from 'types/api-responses';
import style from './supporting-files.module.scss';
import { RelatedWellFilesDeprecationNotice } from './related-well-files-deprecation-notice';

const FileList = ({ children }: { children: ReactNode }) => {
  return (
    <TableContainer>
      <Table size="md" className={style.fileTable}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Owner</TableHeader>
            <TableHeader>File type</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>
  );
};

const File = ({ file }: { file: WellSupportingFileResponse }) => {
  return (
    <TableRow>
      <TableCell>
        <Link
          truncate
          to={file.attachmentHref}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            // For TIFF files, we intercept the click to prevent auto-download
            if (
              ['.tiff', '.tif'].some((ext) =>
                file.fileName.toLowerCase().endsWith(ext)
              )
            ) {
              e.preventDefault();
              window.open(file.attachmentHref, '_blank');
            }
          }}
        >
          {file.fileName}
        </Link>
      </TableCell>
      <TableCell>{file.createdBy}</TableCell>
      <TableCell>{file.fileType}</TableCell>
    </TableRow>
  );
};

const SupportingFilesImpl = ({
  files,
}: {
  files: WellSupportingFileResponse[] | undefined;
}) => {
  return (
    <div className={style.filesContainer}>
      {files ? (
        files.length ? (
          <FileList>
            {files.map((file) => (
              <File key={file.id} file={file} />
            ))}
          </FileList>
        ) : (
          <EmptyState
            icon={DocumentSvg}
            headerText="No Files Available"
            helperText="Currently this well doesn't have any supporting files."
          />
        )
      ) : (
        <DataTableSkeleton
          showHeader={false}
          showToolbar={false}
          columnCount={3}
        />
      )}
    </div>
  );
};

// `hideHeading` drops the section's own header for a frame that draws one (the Well
// page's card).
const SupportingFiles = ({
  href,
  hideHeading = false,
}: {
  href: string;
  hideHeading?: boolean;
}) => {
  const supportingFilesAtom = useSupportingFilesAtom(href);
  const supportingFiles = useAtomValue(supportingFilesAtom);

  const data = supportingFiles.data;
  const errorMessage = data && 'error' in data ? data.error : undefined;
  const files = data && 'error' in data ? undefined : data;

  // The deprecation notice stays visible on every state — including a failed
  // fetch — so users always see where files are moving.
  return (
    <Section
      header={
        hideHeading ? undefined : (
          <>
            <span>{`Related Well Files (${files?.length || 0})`}</span>
            <span className={style.noVersionMarker}>Not Versioned</span>
          </>
        )
      }
    >
      <RelatedWellFilesDeprecationNotice />
      {errorMessage ? (
        <InlineNotification kind="error" title={errorMessage} lowContrast />
      ) : (
        <SupportingFilesImpl files={files} />
      )}
    </Section>
  );
};

export { SupportingFiles };
